// These labels may vary depending on the ValueSet.

import { getExtensionValue } from "@medplum/core";
import { Coding, QuestionnaireItem } from "@medplum/fhirtypes";

import { ComboboxItem } from "@mantine/core";

import { Extensions } from "@/fhir/enums/extensions";
import {
  GenericFormValues,
  getFormsetCodeLinkId,
  getFormsetDisplayLinkId,
} from "@/utils/questionnaires";

// If there's a new value representing an "empty answer", it must be added here.
export const EMPTY_ANSWER_LABELS = ["None", "Decline to answer"];

const EMPTY_ANSWER_CODE = "none";

/**
 * True for answers that mean "nothing applies" — "None" and "Decline to answer".
 *
 * The code is checked as well as the display because the codes are not uniform: Quilted-owned
 * CodeSystems use `<sibling prefix>/none` or a bare `none`, while LOINC-defined lists use
 * `LA137-2`. The display check is what catches the LOINC codes, so both branches are load-bearing.
 */
export const isEmptyAnswerCoding = (coding: Coding | undefined): boolean => {
  if (!coding) return false;
  if (coding.display && EMPTY_ANSWER_LABELS.includes(coding.display)) return true;
  if (!coding.code) return false;

  return coding.code === EMPTY_ANSWER_CODE || coding.code.endsWith(`/${EMPTY_ANSWER_CODE}`);
};

// Multi-answer fields keep their selections as serialized Codings.
export const parseAnswerCoding = (value: string | undefined): Coding => {
  if (!value) return {};

  try {
    return JSON.parse(value) as Coding;
  } catch {
    return {};
  }
};

export const isEmptyAnswerValue = (value: string | undefined): boolean =>
  isEmptyAnswerCoding(parseAnswerCoding(value));

const isEmptyAnswerOption = (option: ComboboxItem): boolean =>
  isEmptyAnswerValue(option.value) || EMPTY_ANSWER_LABELS.includes(option.label);

export const getUpdatedSelectData = (
  data: ComboboxItem[],
  values: string[] | undefined,
  formsetItem: QuestionnaireItem | undefined,
): ComboboxItem[] => {
  if (!values) return data;

  const value = values[values.length - 1];
  // If a excluding option is selected (eg None), then no other option can be selected.
  if (isEmptyAnswerValue(value)) {
    return data.map((option) => {
      if (value !== option.value) return { ...option, disabled: true };
      return option;
    });
  }

  // If a formset is being used and there are values to select, disable what's being selected.
  // This must be done so the user deletes an option only through the formset to avoid losing data.
  if (formsetItem && values.length > 0) {
    return data.map((option) => {
      const disabled = values.includes(option.value) || isEmptyAnswerOption(option);
      return { ...option, disabled };
    });
  }

  // If no option is selected, enable all options.
  if (!values.length) {
    return data.map((option) => ({ ...option, disabled: false }));
  }

  // Disable excluding options if there are other options being selected.
  // This is done so the user can't select "None" after selecting something else, for example.
  return data.map((option) => {
    if (isEmptyAnswerOption(option)) return { ...option, disabled: true };
    return option;
  });
};

export const getUpdatedFormsetValues = (
  formsetItem: QuestionnaireItem,
  formValues: GenericFormValues,
  selectedValues: string[],
) => {
  const newValues: GenericFormValues = {};
  const formsetLinkId = formsetItem.linkId;

  let formsetValues;
  if (!formValues[formsetLinkId]) {
    newValues[formsetLinkId] = {};
    formsetValues = newValues[formsetLinkId];
  } else {
    formsetValues = formValues[formsetLinkId];
  }

  const isFormset = getExtensionValue(formsetItem, Extensions.QuestionnaireItemFormset);

  const latestValue = selectedValues[selectedValues.length - 1];

  // "Empty answers" (eg None) have no details to collect, so they never get a formset row. This
  // check must stay above the isFormset branch, otherwise formset-controlled selects write an
  // empty row keyed by the "None" code.
  if (isEmptyAnswerValue(latestValue)) {
    return newValues;
  }

  const parsedValue = parseAnswerCoding(latestValue);

  if (isFormset) {
    formsetValues[parsedValue.code!] = {};
    return newValues;
  }

  if (parsedValue.code && !formsetValues[latestValue]) {
    const fields = formsetItem!.item!.map((item) => item.linkId);
    const formsetValue: Record<string, string | undefined> = {};
    const labelField = getFormsetDisplayLinkId(formsetItem!.item);
    if (labelField) {
      formsetValue[labelField] = parsedValue.display;
    }

    const valueField = getFormsetCodeLinkId(formsetItem!.item);
    if (valueField) {
      formsetValue[valueField] = parsedValue.code;
    }

    formsetValues[parsedValue.code] = {
      ...formsetValue,
      ...fields
        .filter((field) => ![labelField, valueField].includes(field))
        .reduce((result, field) => ({ ...result, [field]: null }), {}),
    };
  }

  return newValues;
};

export const getFormsetParentPath = (path: string, groupLinkId: string, valueCoding: Coding) => {
  const splittedPath = path.split(".");

  const nestedParentPathName =
    splittedPath.length > 1 ? splittedPath.slice(0, -1)?.join(".") : null;

  const basePath = `${groupLinkId}.${valueCoding.code}`;

  if (nestedParentPathName) {
    return `${nestedParentPathName}.${basePath}`;
  }

  return basePath;
};
